import { useEffect, useState } from "react";
import { Building2, Landmark, Newspaper, Search } from "lucide-react";
import { useTranslation } from "@ncp/i18n";
import { Badge, Input, cn } from "@ncp/ui";
import { useDealCatalog, type CatalogHit } from "./api";

/** Typeahead over the local replica plus licensed-web company suggestions (not the official ION API). */
export function CatalogSearch({ value, onCommit, onOpenDeal, onOpenCompany }: {
  value: string;
  onCommit: (q: string) => void;
  onOpenDeal: (replicaId: string) => void;
  onOpenCompany: (companyId: string) => void;
}) {
  const { t } = useTranslation();
  const [text, setText] = useState(value);
  const [debounced, setDebounced] = useState(value);
  const [open, setOpen] = useState(false);
  useEffect(() => { setText(value); }, [value]);
  useEffect(() => {
    const id = window.setTimeout(() => setDebounced(text.trim()), 300);
    return () => window.clearTimeout(id);
  }, [text]);
  const catalog = useDealCatalog(debounced, true);
  const hits = catalog.data?.hits ?? [];

  const pick = (hit: CatalogHit) => {
    setOpen(false);
    if ((hit.kind === "Deal" || hit.kind === "Intelligence") && hit.replicaId) onOpenDeal(hit.replicaId);
    else if (hit.kind === "Company") onOpenCompany(hit.externalId);
    else if (hit.kind === "News" && hit.replicaId) window.location.assign(`${import.meta.env.BASE_URL}news?item=${hit.replicaId}`);
    else onCommit(hit.headline);
  };

  return (
    <div className="relative ms-auto min-w-72">
      <Search className="pointer-events-none absolute start-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
      <Input
        className="px-9"
        value={text}
        onChange={(e) => { setText(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onKeyDown={(e) => {
          if (e.key === "Enter") { setOpen(false); onCommit(text.trim()); }
          if (e.key === "Escape") setOpen(false);
        }}
        onBlur={() => window.setTimeout(() => setOpen(false), 180)}
        placeholder={t("backoffice:dealWatch.searchPlaceholder")}
        // Empty, the field reads in the page's direction (its placeholder is in the screen's language); typed text takes its own.
        dir={text ? "auto" : undefined}
        role="combobox"
        aria-autocomplete="list"
        aria-expanded={open}
      />
      {open && debounced.length >= 2 ? (
        <div className="absolute inset-x-0 top-full z-30 mt-1 max-h-80 overflow-auto rounded-xl border border-sand-200 bg-white p-1 shadow-lg">
          {catalog.isLoading ? <p className="px-3 py-2 text-xs text-muted-foreground">{t("misc.loading")}</p> : null}
          {hits.length === 0 && !catalog.isLoading ? <p className="px-3 py-2 text-xs text-muted-foreground">{t("backoffice:dealWatch.catalogEmpty")}</p> : null}
          {hits.map((hit) => (
            <button key={`${hit.kind}-${hit.externalId}`} type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => pick(hit)} className={cn("flex w-full items-start gap-2 rounded-lg px-3 py-2 text-start text-sm hover:bg-sand-50")}>
              {hit.kind === "Company" ? <Building2 className="mt-0.5 size-4 shrink-0 text-sand-500" /> : hit.kind === "News" ? <Newspaper className="mt-0.5 size-4 shrink-0 text-sand-500" /> : <Landmark className="mt-0.5 size-4 shrink-0 text-sand-500" />}
              <span className="min-w-0 flex-1">
                <span className="line-clamp-2 font-medium">{hit.headline}</span>
                <span className="mt-0.5 flex flex-wrap items-center gap-1 text-[11px] text-muted-foreground">
                  <Badge size="sm" variant={hit.fromReplica ? "outline" : "gold"}>{t(`backoffice:dealWatch.catalogKind.${hit.kind}`, { defaultValue: hit.kind })}</Badge>
                  <span className="font-mono">{hit.externalId}</span>
                  {hit.fromReplica ? null : <span>{t("backoffice:dealWatch.catalogLive")}</span>}
                </span>
              </span>
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}
